<template>
  <main class="flex-1 overflow-auto">
    <div class="p-6 lg:p-8">
      <!-- Header -->
      <header class="mb-8">
        <h1 class="text-3xl font-bold text-gray-800">Fournisseurs</h1>
        <p class="text-gray-600">Gérez vos partenaires et fournisseurs</p>
      </header>

      <!-- Stats Bar -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div class="bg-white p-4 rounded-lg shadow border">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-500">Total Fournisseurs</p>
              <p class="text-2xl font-bold text-gray-800">{{ suppliersStats.totalSuppliers }}</p>
            </div>
            <div class="p-3 bg-blue-50 rounded-lg">
              <icon name="Building2" :size="20" class="text-blue-600" />
            </div>
          </div>
        </div>
        
        <div class="bg-white p-4 rounded-lg shadow border">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-500">Actifs</p>
              <p class="text-2xl font-bold text-green-600">{{ suppliersStats.activeSuppliers }}</p>
            </div>
            <div class="p-3 bg-green-50 rounded-lg">
              <icon name="CheckCircle" :size="20" class="text-green-600" />
            </div>
          </div>
        </div>
        
        <div class="bg-white p-4 rounded-lg shadow border">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-500">Produits</p>
              <p class="text-2xl font-bold text-purple-600">{{ suppliersStats.totalProductsFromSuppliers }}</p>
            </div>
            <div class="p-3 bg-purple-50 rounded-lg">
              <icon name="Package" :size="20" class="text-purple-600" />
            </div>
          </div>
        </div>
        
        <div class="bg-white p-4 rounded-lg shadow border">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-500">Commandes</p>
              <p class="text-2xl font-bold text-orange-600">{{ suppliersStats.totalOrdersFromSuppliers }}</p>
            </div>
            <div class="p-3 bg-orange-50 rounded-lg">
              <icon name="ShoppingCart" :size="20" class="text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      <!-- Actions Bar -->
      <div class="flex flex-col sm:flex-row gap-4 mb-6">
        <div class="relative flex-1">
          <icon name="Search" class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            placeholder="Rechercher un fournisseur..."
            class="pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            :value="searchTerm"
            @input="searchTerm = $event.target.value"
          />
        </div>
        <button
          @click="openAddModal"
          class="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          <icon name="Plus" :size="16" />
          Nouveau fournisseur
        </button>
      </div>

      <!-- Suppliers Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div
          v-for="supplier in filteredSuppliers"
          :key="supplier.id"
          class="bg-white rounded-xl shadow border border-gray-200 hover:shadow-lg transition-shadow"
        >
          <div class="p-5">
            <div class="flex items-start justify-between mb-4">
              <div>
                <h3 class="font-semibold text-lg text-gray-800">{{ supplier.name }}</h3>
                <span :class="`px-3 py-1 rounded-full text-xs font-medium ${
                  supplier.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`">
                  {{ supplier.status_label || (supplier.status === 'active' ? 'Actif' : 'Inactif') }}
                </span>
              </div>
              <div class="flex items-center space-x-1">
                <span class="text-yellow-500 flex items-center">
                  <icon name="Star" :size="12" class="mr-1" />
                  {{ supplier.rating || 'N/A' }}
                </span>
              </div>
            </div>

            <div class="space-y-2 text-sm mb-4">
              <div class="flex items-center gap-2 text-gray-500">
                <icon name="Mail" :size="16" />
                <span class="truncate">{{ supplier.email }}</span>
              </div>
              <div class="flex items-center gap-2 text-gray-500">
                <icon name="Phone" :size="16" />
                <span>{{ supplier.phone }}</span>
              </div>
              <div class="flex items-center gap-2 text-gray-500">
                <icon name="MapPin" :size="16" />
                <span class="truncate">{{ supplier.address }}</span>
              </div>
              <div v-if="supplier.contact_person" class="flex items-center gap-2 text-gray-500">
                <icon name="User" :size="16" />
                <span>Contact: {{ supplier.contact_person }}</span>
              </div>
            </div>

            <div class="grid grid-cols-3 gap-2 py-3 border-y border-gray-200 mb-4">
              <div class="text-center">
                <div class="flex items-center justify-center gap-1 text-gray-400 mb-1">
                  <icon name="Package" :size="12" />
                </div>
                <p class="font-semibold text-gray-800">{{ supplier.product_count || 0 }}</p>
                <p class="text-xs text-gray-500">Produits</p>
              </div>
              <div class="text-center border-x border-gray-200">
                <div class="flex items-center justify-center gap-1 text-gray-400 mb-1">
                  <icon name="ShoppingCart" :size="12" />
                </div>
                <p class="font-semibold text-gray-800">{{ supplier.order_count || 0 }}</p>
                <p class="text-xs text-gray-500">Commandes</p>
              </div>
              <div class="text-center">
                <p class="font-semibold text-sm text-gray-800">{{ supplier.revenue_formatted || formatPrice(supplier.total_revenue || 0) }}</p>
                <p class="text-xs text-gray-500">CA Total</p>
              </div>
            </div>

            <div class="flex items-center justify-between">
              <router-link :to="`/admin/fournisseurs/${supplier.id}`">
                <button class="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition">
                  <icon name="Eye" :size="16" class="mr-1" /> Voir détails
                </button>
              </router-link>
              <div class="flex space-x-1">
                <button
                  @click="handleEdit(supplier)"
                  class="p-2 hover:bg-gray-100 rounded-lg transition"
                >
                  <icon name="Edit" :size="16" class="text-gray-600" />
                </button>
                <button
                  @click="showDeleteModal = { id: supplier.id, name: supplier.name }"
                  class="p-2 hover:bg-red-50 rounded-lg transition"
                >
                  <icon name="Trash2" :size="16" class="text-red-600" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-12">
        <div class="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <p class="text-gray-600 mt-4">Chargement des fournisseurs...</p>
      </div>

      <!-- Empty State -->
      <div v-if="!loading && filteredSuppliers.length === 0" class="text-center py-12">
        <div class="w-24 h-24 mx-auto mb-4 text-gray-400">
          <icon name="Building2" :size="96" />
        </div>
        <p class="text-lg text-gray-600 mb-2">Aucun fournisseur trouvé</p>
        <p class="text-gray-500 mb-6">{{ searchTerm ? 'Aucun résultat pour votre recherche' : 'Commencez par ajouter votre premier fournisseur' }}</p>
        <button
          @click="openAddModal"
          class="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition mx-auto"
        >
          <icon name="Plus" :size="16" />
          Ajouter un fournisseur
        </button>
      </div>

      <!-- Supplier Modal -->
      <div
        v-if="showModal"
        class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        @click.self="resetForm"
      >
        <div class="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div class="p-6">
            <div class="flex items-center justify-between mb-6">
              <h3 class="text-lg font-semibold text-gray-800">
                {{ editingSupplier ? 'Modifier le fournisseur' : 'Nouveau fournisseur' }}
              </h3>
              <button
                @click="resetForm"
                class="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <icon name="X" :size="20" class="text-gray-500" />
              </button>
            </div>

            <form @submit.prevent="handleSubmit" class="space-y-4">
              <!-- Logo -->
              <div class="flex items-center justify-center mb-6">
                <div class="relative">
                  <div class="w-32 h-32 rounded-lg bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden">
                    <img
                      v-if="supplierForm.logoPreview"
                      :src="supplierForm.logoPreview"
                      alt="Logo preview"
                      class="w-full h-full object-contain p-2"
                    />
                    <div v-else class="text-center">
                      <icon name="Building2" :size="32" class="text-gray-400 mx-auto mb-2" />
                      <p class="text-xs text-gray-500">Logo</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    @click="$refs.logoInput.click()"
                    class="absolute -bottom-2 -right-2 bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 transition"
                  >
                    <icon name="Upload" :size="16" />
                  </button>
                  <input
                    type="file"
                    ref="logoInput"
                    @change="handleLogoUpload"
                    accept="image/*"
                    class="hidden"
                  />
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <!-- Informations de base -->
                <div class="md:col-span-2">
                  <h4 class="font-medium text-gray-700 mb-3">Informations de base</h4>
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Nom du fournisseur *
                  </label>
                  <input
                    v-model="supplierForm.name"
                    required
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    v-model="supplierForm.email"
                    required
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone *
                  </label>
                  <input
                    v-model="supplierForm.phone"
                    required
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Personne de contact
                  </label>
                  <input
                    v-model="supplierForm.contact_person"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div class="md:col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Adresse
                  </label>
                  <textarea
                    v-model="supplierForm.address"
                    rows="2"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  ></textarea>
                </div>

                <!-- Informations supplémentaires -->
                <div class="md:col-span-2 mt-6">
                  <h4 class="font-medium text-gray-700 mb-3">Informations supplémentaires</h4>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Site web
                  </label>
                  <input
                    type="url"
                    v-model="supplierForm.website"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Numéro fiscal
                  </label>
                  <input
                    v-model="supplierForm.tax_number"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Conditions de paiement
                  </label>
                  <input
                    v-model="supplierForm.payment_terms"
                    placeholder="30 jours net"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Compte bancaire
                  </label>
                  <input
                    v-model="supplierForm.bank_account"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Délai de livraison (jours)
                  </label>
                  <input
                    type="number"
                    v-model="supplierForm.lead_time"
                    min="1"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Commande minimale (FCFA)
                  </label>
                  <input
                    type="number"
                    v-model="supplierForm.minimum_order_value"
                    min="0"
                    step="0.01"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Note (0-5)
                  </label>
                  <input
                    type="number"
                    v-model="supplierForm.rating"
                    min="0"
                    max="5"
                    step="0.1"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Statut
                  </label>
                  <select
                    v-model="supplierForm.status"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="active">Actif</option>
                    <option value="inactive">Inactif</option>
                  </select>
                </div>

                <!-- Dates de contrat -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Début du contrat
                  </label>
                  <input
                    type="date"
                    v-model="supplierForm.contract_start_date"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Fin du contrat
                  </label>
                  <input
                    type="date"
                    v-model="supplierForm.contract_end_date"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div class="md:col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    v-model="supplierForm.notes"
                    rows="3"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  ></textarea>
                </div>
              </div>

              <div class="flex space-x-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  @click="resetForm"
                  class="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  :disabled="saving"
                  class="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span v-if="saving">
                    <icon name="Loader" class="animate-spin inline-block mr-2" :size="16" />
                    Enregistrement...
                  </span>
                  <span v-else>
                    {{ editingSupplier ? 'Enregistrer' : 'Ajouter' }}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- Delete Modal -->
      <div
        v-if="showDeleteModal"
        class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        @click.self="showDeleteModal = null"
      >
        <div class="bg-white rounded-xl shadow-lg w-full max-w-sm">
          <div class="p-6 text-center">
            <icon name="Trash2" :size="48" class="mx-auto text-red-500 mb-4" />
            <h3 class="text-lg font-semibold text-gray-800 mb-2">
              Confirmer la suppression
            </h3>
            <p class="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer "{{ showDeleteModal.name }}" ?
              <span class="block text-sm text-red-500 mt-1">
                Cette action est irréversible
              </span>
            </p>
            <div class="flex space-x-3">
              <button
                @click="showDeleteModal = null"
                class="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Annuler
              </button>
              <button
                @click="handleDelete"
                :disabled="deleting"
                class="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="deleting">
                  <icon name="Loader" class="animate-spin inline-block mr-2" :size="16" />
                  Suppression...
                </span>
                <span v-else>Supprimer</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'

export default {
  name: 'SuppliersPage',

  data() {
    return {
      showModal: false,
      showDeleteModal: null,
      editingSupplier: null,
      searchTerm: '',
      loading: false,
      saving: false,
      deleting: false,
      
      supplierForm: {
        name: '',
        email: '',
        phone: '',
        address: '',
        contact_person: '',
        website: '',
        logo: null,
        logoPreview: null,
        notes: '',
        status: 'active',
        payment_terms: '',
        tax_number: '',
        bank_account: '',
        contract_start_date: '',
        contract_end_date: '',
        rating: 0,
        lead_time: 7,
        minimum_order_value: 0
      }
    }
  },

  computed: {
    ...mapState({
      adminSuppliers: state => state.adminSuppliers,
    }),
    
    ...mapGetters([
      'adminSuppliersStats'
    ]),
    
    suppliersStats() {
      return this.adminSuppliersStats || {
        totalSuppliers: 0,
        activeSuppliers: 0,
        inactiveSuppliers: 0,
        totalProductsFromSuppliers: 0,
        totalOrdersFromSuppliers: 0
      }
    },
    
    filteredSuppliers() {
      if (!this.adminSuppliers) return []
      
      return this.adminSuppliers.filter(supplier => 
        supplier.name.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        supplier.email.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        supplier.phone?.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        supplier.contact_person?.toLowerCase().includes(this.searchTerm.toLowerCase())
      )
    }
  },

  methods: {
    ...mapActions([
      'fetchAdminSuppliers',
      'saveSupplier',
      'deleteSupplier'
    ]),
    
    formatPrice(price) {
      return new Intl.NumberFormat('fr-FR').format(price) + ' FCFA'
    },

    async loadSuppliers() {
      try {
        this.loading = true
        await this.fetchAdminSuppliers()
      } catch (error) {
        console.error('Erreur lors du chargement des fournisseurs:', error)
        this.$emit('notify', {
          title: 'Erreur',
          message: 'Impossible de charger les fournisseurs',
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },

    openAddModal() {
      this.editingSupplier = null
      this.resetFormData()
      this.showModal = true
    },

    resetFormData() {
      this.supplierForm = {
        name: '',
        email: '',
        phone: '',
        address: '',
        contact_person: '',
        website: '',
        logo: null,
        logoPreview: null,
        notes: '',
        status: 'active',
        payment_terms: '',
        tax_number: '',
        bank_account: '',
        contract_start_date: '',
        contract_end_date: '',
        rating: 0,
        lead_time: 7,
        minimum_order_value: 0
      }
    },

    resetForm() {
      this.showModal = false
      this.editingSupplier = null
      this.resetFormData()
    },

    handleLogoUpload(event) {
      const file = event.target.files[0]
      if (file) {
        this.supplierForm.logo = file
        const reader = new FileReader()
        reader.onload = (e) => {
          this.supplierForm.logoPreview = e.target.result
        }
        reader.readAsDataURL(file)
      }
    },

    async handleSubmit() {
      try {
        this.saving = true
        
        const formData = new FormData()
        
        // Ajouter les champs
        Object.keys(this.supplierForm).forEach(key => {
          if (key !== 'logo' && key !== 'logoPreview' && this.supplierForm[key] !== null && this.supplierForm[key] !== undefined) {
            formData.append(key, this.supplierForm[key])
          }
        })
        
        // Ajouter l'ID si modification
        if (this.editingSupplier) {
          formData.append('id', this.editingSupplier.id)
        }
        
        // Ajouter le logo s'il y en a un nouveau
        if (this.supplierForm.logo instanceof File) {
          formData.append('logo', this.supplierForm.logo)
        }
        
        await this.saveSupplier(formData)
        
        this.$emit('notify', {
          title: 'Succès',
          message: this.editingSupplier ? 'Fournisseur mis à jour' : 'Fournisseur créé',
          type: 'success'
        })
        
        this.resetForm()
        this.loadSuppliers()
      } catch (error) {
        console.error('Erreur lors de la sauvegarde du fournisseur:', error)
        this.$emit('notify', {
          title: 'Erreur',
          message: error.response?.data?.message || 'Erreur lors de la sauvegarde',
          type: 'error'
        })
      } finally {
        this.saving = false
      }
    },

    handleEdit(supplier) {
      this.editingSupplier = supplier
      this.supplierForm = {
        name: supplier.name,
        email: supplier.email,
        phone: supplier.phone,
        address: supplier.address,
        contact_person: supplier.contact_person,
        website: supplier.website,
        logo: null,
        logoPreview: supplier.logo,
        notes: supplier.notes,
        status: supplier.status,
        payment_terms: supplier.payment_terms,
        tax_number: supplier.tax_number,
        bank_account: supplier.bank_account,
        contract_start_date: supplier.contract_start_date,
        contract_end_date: supplier.contract_end_date,
        rating: supplier.rating,
        lead_time: supplier.lead_time,
        minimum_order_value: supplier.minimum_order_value
      }
      this.showModal = true
    },

    async handleDelete() {
      try {
        this.deleting = true
        await this.deleteSupplier(this.showDeleteModal.id)
        
        this.$emit('notify', {
          title: 'Succès',
          message: 'Fournisseur supprimé',
          type: 'success'
        })
        
        this.showDeleteModal = null
        this.loadSuppliers()
      } catch (error) {
        console.error('Erreur lors de la suppression du fournisseur:', error)
        this.$emit('notify', {
          title: 'Erreur',
          message: error.response?.data?.message || 'Erreur lors de la suppression',
          type: 'error'
        })
      } finally {
        this.deleting = false
      }
    }
  },

  async mounted() {
    await this.loadSuppliers()
  }
}
</script>

<style scoped>
/* Animation pour les cartes */
.bg-white {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.bg-white:hover {
  transform: translateY(-2px);
}

/* Animation pour les modales */
.fixed {
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Style pour les boutons */
button {
  transition: all 0.2s ease;
}

button:hover:not(:disabled) {
  transform: translateY(-1px);
}

/* Animation de spin */
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>